import { useEffect, useCallback, useRef } from 'react';
import { expValEquals } from '@confluence/feature-experiments';

export class WaitForPublishButtonComponentUnmountedError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'WaitForPublishButtonComponentUnmountedError';
	}
}
export class WaitForPublishButtonPreviousCallNotCompletedError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'WaitForPublishButtonPreviousCallNotCompletedError';
	}
}
export class WaitForPublishButtonTimeoutError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'WaitForPublishButtonTimeoutError';
	}
}
export class WaitForPublishButtonTabInactiveError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'WaitForPublishButtonTabInactiveError';
	}
}

const PUBLISH_BUTTON_SELECTOR =
	'[data-testid="publish-modal-update-button"], [data-testid="publish-button"], [data-testid="scheduled-button"], [data-testid="publish-button-ui"]';

// Interval in milliseconds between checks for the publish button to be enabled.
const CHECK_INTERVAL = 2_000;

// These many milliseconds after checks start, the experience will timeout and fail.
const EXP_TIMEOUT_LENGTH = 20_000;
const ANALYTICS_ONLY_TIMEOUT_LENGTH = 20_000;
const getTimeoutLength = () => {
	return expValEquals('cc_delay_succeeding_edit_page_until_publish_button', 'isEnabled', true)
		? EXP_TIMEOUT_LENGTH
		: ANALYTICS_ONLY_TIMEOUT_LENGTH;
};

const getPublishButton = () => {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	return document.querySelector(PUBLISH_BUTTON_SELECTOR);
};
const isPublishButtonEnabled = () => {
	const publishButton = getPublishButton();
	return publishButton && (publishButton as HTMLButtonElement).disabled === false;
};

const getTimeoutErrorMessage = () => {
	let errorMessage = '';
	if (!getPublishButton()) {
		errorMessage = 'Did not find a publish button before timeout';
	} else if (!isPublishButtonEnabled()) {
		errorMessage = 'Found a publish button, but it was not enabled before timeout';
	} else {
		errorMessage = 'Found an enabled publish button.  This should not be an error.';
	}
	return errorMessage;
};

/**
 * React hook that provides a function to wait for the publish button to become enabled.
 * Automatically cleans up intervals and timeouts when the component unmounts.
 *
 * @returns A function `waitForPublishButtonToBeEnabled` returning a Promise which settles in the following ways:
 * - Resolves when it finds a publish button which is enabled in the DOM
 * - Rejects with a `WaitForPublishButtonTimeoutError` if the publish button is not enabled after 10 seconds
 * - Rejects with a `WaitForPublishButtonComponentUnmountedError` when the component unmounts before the previous call found the publish butto
 * - Rejects with a `WaitForPublishButtonPreviousCallNotCompletedError` when `waitForPublishButtonToBeEnabled` is called again before the previous call found the publish button
 */
export const useWaitForPublishButtonToBeEnabled = (): (() => Promise<void>) => {
	const checkerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const currentPromiseRejectFunctionRef = useRef<((reason?: Error) => void) | null>(null);
	const visibilityChangeListenerRef = useRef<(() => void) | null>(null);

	const rejectPromiseDueToUnmount = useCallback(() => {
		if (currentPromiseRejectFunctionRef.current !== null) {
			currentPromiseRejectFunctionRef.current(
				new WaitForPublishButtonComponentUnmountedError(
					'Component unmounted before the publish button was enabled',
				),
			);
		}
	}, []);
	const rejectPromiseDueToRepeatedCall = useCallback(() => {
		if (currentPromiseRejectFunctionRef.current !== null) {
			currentPromiseRejectFunctionRef.current(
				new WaitForPublishButtonPreviousCallNotCompletedError(
					'waitForPublishButtonToBeEnabled was called again before the publish button was enabled',
				),
			);
		}
	}, []);
	const cleanupIntervals = useCallback(() => {
		if (checkerIntervalRef.current !== null) {
			clearInterval(checkerIntervalRef.current);
			checkerIntervalRef.current = null;
		}
		if (timeoutRef.current !== null) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		if (visibilityChangeListenerRef.current !== null) {
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			document.removeEventListener('visibilitychange', visibilityChangeListenerRef.current);
			visibilityChangeListenerRef.current = null;
		}
	}, []);

	// Reject the pending Promise and cancel timeouts and intervals on unmount
	useEffect(() => {
		return () => {
			cleanupIntervals();
			rejectPromiseDueToUnmount();
		};
	}, [cleanupIntervals, rejectPromiseDueToUnmount]);

	return useCallback(() => {
		// Clean up any existing intervals/timeouts
		cleanupIntervals();
		rejectPromiseDueToRepeatedCall();

		return new Promise<void>((resolve, reject) => {
			// Save this reject function so we can later call it if the component unmounts.
			currentPromiseRejectFunctionRef.current = reject;

			// Check if button is already enabled
			if (isPublishButtonEnabled()) {
				resolve();
				return;
			}

			// Check if this tab is currently inactive.
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			if (document.visibilityState === 'hidden') {
				reject(
					new WaitForPublishButtonTabInactiveError(
						'Could not search for the publish button because this tab is inactive',
					),
				);
				return;
			}

			// Add an event listener to check if the tab become inactive while waiting for the publish button to be enabled.
			const visibilityChangeEventListener = () => {
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				if (document.visibilityState === 'hidden') {
					cleanupIntervals();
					reject(
						new WaitForPublishButtonTabInactiveError(
							'Could not search for the publish button because this tab became inactive',
						),
					);
				}
			};
			visibilityChangeListenerRef.current = visibilityChangeEventListener;
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			document.addEventListener('visibilitychange', visibilityChangeEventListener);

			// Set up interval to continuously check button state
			checkerIntervalRef.current = setInterval(() => {
				if (isPublishButtonEnabled()) {
					cleanupIntervals();
					resolve();
				}
			}, CHECK_INTERVAL);

			// Set up timeout to reject after TIMEOUT_LENGTH
			timeoutRef.current = setTimeout(() => {
				cleanupIntervals();
				if (isPublishButtonEnabled()) {
					resolve();
				} else {
					reject(new WaitForPublishButtonTimeoutError(getTimeoutErrorMessage()));
				}
			}, getTimeoutLength());
		});
	}, [cleanupIntervals, rejectPromiseDueToRepeatedCall]);
};
